<template>
  <div class="page-card">
    <div class="page-head">
      <h3>PBSQ Post-Session Assessment Records</h3>
    </div>

    <el-form :inline="true" :model="query" size="small" class="search-form">
      <el-form-item label="Keyword">
        <el-input v-model="query.keyword" clearable placeholder="User ID / name / answer content" @keyup.enter.native="search" />
      </el-form-item>
      <el-form-item label="PBSQ">
        <el-input-number v-model="query.scoreMin" :min="0" :max="100" placeholder="Min" />
        <span class="range-sep">-</span>
        <el-input-number v-model="query.scoreMax" :min="0" :max="100" placeholder="Max" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">Search</el-button>
        <el-button @click="resetSearch">Reset</el-button>
      </el-form-item>
    </el-form>

    <el-table :data="list" v-loading="loading" border size="small">
      <el-table-column prop="id" label="ID" width="70" />
      <el-table-column label="User ID" min-width="150">
        <template slot-scope="scope">{{ formatUserId(scope.row) }}</template>
      </el-table-column>
      <el-table-column prop="checkinType" label="Type" width="100" />
      <el-table-column prop="pbsqScore" label="PBSQ" width="90" />
      <el-table-column prop="orsScore" label="ORS" width="80" />
      <el-table-column prop="moiScore" label="MOI" width="80" />
      <el-table-column label="Answer Summary" min-width="300" show-overflow-tooltip>
        <template slot-scope="scope">
          <span>{{ answerSummary(scope.row) }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="createTime" label="Created At" min-width="160" />
      <el-table-column label="Actions" width="150" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="openDetail(scope.row)">Details</el-button>
          <el-button type="text" size="small" class="danger" @click="remove(scope.row)">Delete</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="pager"
      background
      layout="total, sizes, prev, pager, next"
      :total="total"
      :page-size="query.pageSize"
      :current-page="query.pageNum"
      :page-sizes="[10, 20, 50, 100]"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />

    <el-dialog title="PBSQ Post-Session Assessment Details" :visible.sync="detailVisible" width="900px">
      <el-descriptions v-if="detailRecord" :column="3" size="small" border class="detail-head">
        <el-descriptions-item label="ID">{{ detailRecord.id }}</el-descriptions-item>
        <el-descriptions-item label="User ID">{{ formatUserId(detailRecord) }}</el-descriptions-item>
        <el-descriptions-item label="Type">{{ detailRecord.checkinType }}</el-descriptions-item>
        <el-descriptions-item label="PBSQ">{{ detailRecord.pbsqScore }}</el-descriptions-item>
        <el-descriptions-item label="ORS">{{ detailRecord.orsScore }}</el-descriptions-item>
        <el-descriptions-item label="MOI">{{ detailRecord.moiScore }}</el-descriptions-item>
        <el-descriptions-item label="Created At" :span="3">{{ detailRecord.createTime }}</el-descriptions-item>
      </el-descriptions>

      <el-table :data="detailAnswers" border size="small" class="answer-table">
        <el-table-column prop="step" label="Step" width="190" />
        <el-table-column prop="title" label="Title" min-width="240" />
        <el-table-column prop="answer" label="Answer" min-width="280" />
        <el-table-column prop="rawValue" label="Raw Value" width="100" />
      </el-table>

      <el-collapse class="raw-collapse">
        <el-collapse-item title="Raw Answers JSON" name="raw">
          <pre class="raw-json">{{ prettyAnswersJson }}</pre>
        </el-collapse-item>
      </el-collapse>

      <span slot="footer">
        <el-button type="primary" @click="detailVisible=false">Close</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { pbsqApi } from '@/api/health'
import { formatUserId } from '@/utils/userDisplay'

const emptyQuery = () => ({ pageNum: 1, pageSize: 10, keyword: '', checkinType: 'full', scoreMin: undefined, scoreMax: undefined })

const QUESTION_LIST = [
  { id: 'A1', step: 'Domain A - Cardiovascular', title: 'A1 - Heart Rate Recovery', options: ['Very fast - under 2 minutes', 'Normal - 2-5 minutes', 'Slow - 5-10 minutes'] },
  { id: 'A2', step: 'Domain A - Cardiovascular', title: 'A2 - Exercise HR Ceiling', options: ['Yes - well within expected range', 'Slightly elevated for effort', 'Disproportionately high'] },
  { id: 'A3', step: 'Domain A - Cardiovascular', title: 'A3 - Post-Exercise Palpitations', options: ['None', 'Mild, brief skips', 'Frequent or prolonged - FLAG'] },
  { id: 'B1', step: 'Domain B - Breathlessness', title: 'B1 - Borg Dyspnoea Scale', options: ['Very mild (0-2)', 'Moderate (3-4)', 'Severe (5-7)', 'Very severe (8-10)'] },
  { id: 'B2', step: 'Domain B - Breathlessness', title: 'B2 - Recovery Breathing', options: ['Under 2 minutes', '2-5 minutes', 'Over 5 minutes'] },
  { id: 'C1', step: 'Domain C - Fatigue', title: 'C1 - Session RPE (Foster)', options: ['Light (1-3)', 'Moderate (4-6)', 'Hard (7-8)', 'Maximal (9-10)'] },
  { id: 'C2', step: 'Domain C - Fatigue', title: 'C2 - Mental Fatigue', options: ['Fresh and alert', 'Some mental fatigue', 'Significantly drained'] },
  { id: 'C3', step: 'Domain C - Fatigue', title: 'C3 - Sleep Impact', options: ['Normal recovery sleep', 'May affect sleep', 'Poor sleep likely'] },
  { id: 'D1', step: 'Domain D - Muscle Soreness', title: 'D1 - Overall Muscle Soreness', options: ['None', 'Mild', 'Moderate', 'Severe'] },
  { id: 'D2', step: 'Domain D - Muscle Soreness', title: 'D2 - Localised Soreness', options: ['No localised soreness', '1-2 sites mild', 'Multiple sites moderate+'] },
  { id: 'D3', step: 'Domain D - Muscle Soreness', title: 'D3 - Functional Impact', options: ['Full range', 'Slight stiffness', 'Limited movement'] },
  { id: 'E1', step: 'Domain E - Joint Pain', title: 'E1 - Any Joint Pain?', options: ['No pain', 'Mild discomfort', 'Moderate pain', 'Severe pain - FLAG'] },
  { id: 'E2', step: 'Domain E - Joint Pain', title: 'E2 - Pain Location', options: ['N/A - no pain', 'Lower body', 'Upper body', 'Multiple sites'] }
]

const QUESTION_MAP = QUESTION_LIST.reduce((map, item) => {
  map[item.id] = item
  return map
}, {})

export default {
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      query: emptyQuery(),
      detailVisible: false,
      detailRecord: null
    }
  },
  computed: {
    detailAnswers() {
      if (!this.detailRecord) return []
      return this.answerRows(this.detailRecord)
    },
    prettyAnswersJson() {
      if (!this.detailRecord) return '{}'
      const answers = this.parseAnswers(this.detailRecord.answersJson)
      return JSON.stringify(answers, null, 2)
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    formatUserId,
    async loadData() {
      this.loading = true
      try {
        const res = await pbsqApi.list(this.query)
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    search() {
      this.query.pageNum = 1
      this.loadData()
    },
    resetSearch() {
      this.query = emptyQuery()
      this.loadData()
    },
    handleSizeChange(size) {
      this.query.pageSize = size
      this.query.pageNum = 1
      this.loadData()
    },
    handleCurrentChange(page) {
      this.query.pageNum = page
      this.loadData()
    },
    openDetail(row) {
      this.detailRecord = { ...row }
      this.detailVisible = true
    },
    parseAnswers(value) {
      if (!value) return {}
      if (typeof value === 'object') {
        return value.answers && typeof value.answers === 'object' ? value.answers : value
      }
      try {
        const parsed = JSON.parse(value)
        if (!parsed || typeof parsed !== 'object') return {}
        return parsed.answers && typeof parsed.answers === 'object' ? parsed.answers : parsed
      } catch (e) {
        return {}
      }
    },
    answerRows(row) {
      const answers = this.parseAnswers(row.answersJson)
      const knownRows = QUESTION_LIST
        .filter(item => Object.prototype.hasOwnProperty.call(answers, item.id))
        .map(item => this.formatAnswerRow(item, answers[item.id]))
      const unknownRows = Object.keys(answers)
        .filter(key => !QUESTION_MAP[key])
        .map(key => ({
          step: 'Other',
          title: key,
          answer: this.stringifyAnswer(answers[key]),
          rawValue: this.stringifyAnswer(answers[key])
        }))
      return [...knownRows, ...unknownRows]
    },
    formatAnswerRow(question, value) {
      const numericValue = Number(value)
      const hasNumericValue = value !== null && value !== '' && !Number.isNaN(numericValue)
      const option = hasNumericValue ? question.options[numericValue] : ''
      return {
        step: question.step,
        title: question.title,
        answer: option || this.stringifyAnswer(value),
        rawValue: this.stringifyAnswer(value)
      }
    },
    stringifyAnswer(value) {
      if (value === undefined || value === null) return ''
      if (typeof value === 'object') return JSON.stringify(value)
      return String(value)
    },
    answerSummary(row) {
      const rows = this.answerRows(row)
      if (!rows.length) return 'No answers'
      const summary = rows.slice(0, 3).map(item => `${item.title}: ${item.answer}`).join('; ')
      return rows.length > 3 ? `${summary} ...` : summary
    },
    async remove(row) {
      await this.$confirm(`Delete PBSQ record #${row.id}?`, 'Confirm', { type: 'warning' })
      await pbsqApi.remove(row.id)
      this.$message.success('Deleted successfully')
      this.loadData()
    }
  }
}
</script>

<style scoped>
.page-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.page-head h3 { margin: 0; }
.search-form { margin-bottom: 12px; }
.range-sep { display: inline-block; margin: 0 8px; color: #909399; }
.pager { margin-top: 16px; text-align: right; }
.danger { color: #f56c6c; }
.detail-head { margin-bottom: 16px; }
.answer-table { margin-top: 12px; }
.raw-collapse { margin-top: 16px; }
.raw-json {
  margin: 0;
  padding: 12px;
  color: #303133;
  background: #f7f8fa;
  border-radius: 4px;
  font-family: Consolas, Monaco, monospace;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
